/**
 * MonthlyBudgetOverridePage (월별 예산 조정 화면)
 *
 * 특정 월에 대한 예산 오버라이드를 설정하는 페이지
 *
 * 책임:
 * - 특정 월의 예산 오버라이드 관리
 * - 템플릿 예산 vs 월별 예산 토글
 * - 월 선택 및 네비게이션
 * - 템플릿 대비 비교 표시
 * - 템플릿으로 초기화 기능
 *
 * 핵심 로직:
 * - useMonthlyBudgetOverride hook 사용
 * - 오버라이드 ON: 월별 예산 생성 (year: X, month: Y)
 * - 오버라이드 OFF: 월별 예산 삭제 (템플릿으로 폴백)
 */

import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useCategoryCRUD } from '@/domains/category/hooks/useCategoryCRUD'
import { useMonthlyBudgetOverride } from '../hooks/useMonthlyBudgetOverride'
import { MonthlyBudgetCategoryCard } from '../components/MonthlyBudgetCategoryCard'
import { MonthNavigation } from '@/domains/transaction/components/MonthNavigation'
import { useToast } from '@/shared/components/ui/Toast'

export function MonthlyBudgetOverridePage() {
  const navigate = useNavigate()
  const toast = useToast()
  const [searchParams, setSearchParams] = useSearchParams()

  /**
   * URL 파라미터에서 year/month 가져오기
   * 기본값: 현재 년월
   */
  const now = new Date()
  const initialYear = parseInt(
    searchParams.get('year') || String(now.getFullYear())
  )
  const initialMonth = parseInt(
    searchParams.get('month') || String(now.getMonth() + 1)
  )

  const [year, setYear] = useState(initialYear)
  const [month, setMonth] = useState(initialMonth)

  /**
   * 카테고리 데이터 로드
   */
  const {
    data: allCategories,
    loading: categoriesLoading,
    loadByType,
  } = useCategoryCRUD()

  /**
   * 월별 예산 오버라이드 Hook
   */
  const {
    templateBudgets,
    monthlyBudgets,
    overrideStates,
    budgetAmounts,
    toggleOverride,
    updateBudgetAmount,
    resetToTemplate,
    saveBudgets,
    loading,
    saving,
    error,
  } = useMonthlyBudgetOverride(year, month)

  /**
   * 카테고리 펼침 상태
   */
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set()
  )

  /**
   * 총 예산 계산 (오버라이드 적용된 금액)
   */
  const totalBudget = useMemo(() => {
    return Object.values(budgetAmounts).reduce(
      (sum, amount) => sum + (amount || 0),
      0
    )
  }, [budgetAmounts])

  /**
   * 템플릿 총 예산 계산
   */
  const totalTemplateBudget = useMemo(() => {
    return templateBudgets.reduce(
      (sum, budget) => sum + budget.amount,
      0
    )
  }, [templateBudgets])

  /**
   * 지출 카테고리 로드
   */
  useEffect(() => {
    loadByType('expense')
  }, [])

  /**
   * 월 변경 핸들러
   */
  const handleMonthChange = (newYear: number, newMonth: number) => {
    setYear(newYear)
    setMonth(newMonth)
    // URL 파라미터 업데이트
    setSearchParams({ year: String(newYear), month: String(newMonth) })
  }

  /**
   * 저장 핸들러
   */
  const handleSave = async () => {
    try {
      await saveBudgets()
      toast.success('예산이 저장되었습니다')
      navigate('/status')
    } catch (err) {
      console.error('예산 저장 실패:', err)
      toast.error(
        `저장 실패: ${err instanceof Error ? err.message : '알 수 없는 오류'}`
      )
    }
  }

  /**
   * 템플릿으로 초기화 핸들러
   */
  const handleReset = async () => {
    if (
      !window.confirm(
        `${year}년 ${month}월의 모든 예산 오버라이드를 삭제하고 템플릿으로 초기화하시겠습니까?`
      )
    ) {
      return
    }

    try {
      await resetToTemplate()
      toast.success('템플릿으로 초기화되었습니다')
    } catch (err) {
      console.error('초기화 실패:', err)
      toast.error(
        `초기화 실패: ${err instanceof Error ? err.message : '알 수 없는 오류'}`
      )
    }
  }

  /**
   * 취소 핸들러
   */
  const handleCancel = () => {
    navigate('/status')
  }

  /**
   * 카테고리 펼침/접힘 토글
   */
  const handleToggle = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev)
      if (next.has(categoryId)) {
        next.delete(categoryId)
      } else {
        next.add(categoryId)
      }
      return next
    })
  }

  /**
   * 로딩 상태
   */
  if (loading || categoriesLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-500 mb-4" />
          <p className="text-gray-600 dark:text-gray-400">로딩 중...</p>
        </div>
      </div>
    )
  }

  /**
   * 에러 상태
   */
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-w-md w-full">
          <div className="text-center mb-4">
            <span className="text-6xl">⚠️</span>
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2 text-center">
            데이터를 불러올 수 없습니다
          </h2>
          <p className="text-red-600 text-sm text-center mb-4">
            {error.message}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="w-full px-4 py-2 bg-primary-600 dark:bg-primary-500 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors"
          >
            다시 시도
          </button>
        </div>
      </div>
    )
  }

  /**
   * 대분류 카테고리 목록 (지출만)
   */
  const majorCategories = allCategories.filter(
    category => category.type === 'expense' && !category.parentId
  )

  /**
   * 메인 컨텐츠
   */
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24">
      {/* 헤더 */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-4">
        <button
          onClick={handleCancel}
          className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mb-2 flex items-center gap-1"
        >
          ← 뒤로 가기
        </button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {year}년 {month}월 예산 설정
        </h1>
      </div>

      {/* 안내 메시지 */}
      <div className="px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-800">
        <p className="text-sm text-blue-800 dark:text-blue-200 mb-1">
          💡 이번 달만 적용되는 예산입니다
        </p>
        <p className="text-xs text-blue-600 dark:text-blue-300">
          템플릿 예산: {totalTemplateBudget.toLocaleString()}원
        </p>
      </div>

      {/* 월 선택기 */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
        <MonthNavigation
          year={year}
          month={month}
          onPrevMonth={() => {
            const newMonth = month === 1 ? 12 : month - 1
            const newYear = month === 1 ? year - 1 : year
            handleMonthChange(newYear, newMonth)
          }}
          onNextMonth={() => {
            const newMonth = month === 12 ? 1 : month + 1
            const newYear = month === 12 ? year + 1 : year
            handleMonthChange(newYear, newMonth)
          }}
        />
      </div>

      {/* 총 예산 표시 */}
      <div className="px-4 py-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            총 예산
          </span>
          <div className="text-right">
            <span className="text-lg font-bold text-gray-900 dark:text-white block">
              {totalBudget.toLocaleString()}원
            </span>
            {totalBudget !== totalTemplateBudget && (
              <span
                className={`text-xs ${
                  totalBudget > totalTemplateBudget
                    ? 'text-orange-600 dark:text-orange-400'
                    : 'text-green-600 dark:text-green-400'
                }`}
              >
                템플릿 대비{' '}
                {totalBudget > totalTemplateBudget ? '+' : ''}
                {(totalBudget - totalTemplateBudget).toLocaleString()}원
              </span>
            )}
          </div>
        </div>
      </div>

      {/* 카테고리별 예산 입력 */}
      <div className="px-4 py-6 pb-32 space-y-3">
        {majorCategories.map(category => {
          const templateBudget =
            templateBudgets.find(b => b.categoryId === category.id) || null
          const monthlyBudget =
            monthlyBudgets.find(b => b.categoryId === category.id) || null
          const isOverride = overrideStates[category.id] || false
          const amount = budgetAmounts[category.id] || 0

          return (
            <MonthlyBudgetCategoryCard
              key={category.id}
              category={category}
              templateBudget={templateBudget}
              monthlyBudget={monthlyBudget}
              isOverride={isOverride}
              amount={amount}
              onToggleOverride={() => toggleOverride(category.id)}
              onAmountChange={val => updateBudgetAmount(category.id, val)}
              minorCategories={category.children || []}
              minorAmounts={budgetAmounts}
              minorOverrideStates={overrideStates}
              onMinorToggleOverride={minorId => toggleOverride(minorId)}
              onMinorAmountChange={(minorId, val) =>
                updateBudgetAmount(minorId, val)
              }
              expanded={expandedCategories.has(category.id)}
              onToggle={() => handleToggle(category.id)}
            />
          )
        })}
      </div>

      {/* 저장/취소/초기화 버튼 (고정) - 하단 탭 위에 표시하기 위해 bottom-14 사용 */}
      <div className="fixed bottom-14 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4 shadow-lg z-40">
        <div className="max-w-4xl mx-auto space-y-2">
          {/* 저장/취소 버튼 */}
          <div className="flex gap-3">
            <button
              onClick={handleCancel}
              disabled={saving}
              className="flex-1 px-4 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              취소
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 px-4 py-3 bg-primary-600 dark:bg-primary-500 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {saving ? '저장 중...' : '저장'}
            </button>
          </div>

          {/* 템플릿으로 초기화 버튼 */}
          <button
            onClick={handleReset}
            disabled={saving || monthlyBudgets.length === 0}
            className="w-full px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium border border-red-200 dark:border-red-800"
          >
            템플릿으로 초기화
          </button>
        </div>
      </div>
    </div>
  )
}
